import { useEffect, useState } from "react";
import { Activity, Bot, Clock3, ShieldCheck } from "lucide-react";
import { AgentOSLayout } from "@/components/agent-os/AgentOSLayout";
import { apiFetch, formatDateTime, Pill, Spinner } from "@/components/agent-os/shared";

const Stat = ({ icon: Icon, label, value, testid }) => (
    <div data-testid={testid} className="border border-ink/10 bg-ivory p-6">
        <Icon size={19} strokeWidth={1.5} className="text-forest" />
        <p className="mt-7 font-serif text-4xl font-medium">{value}</p>
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/45">{label}</p>
    </div>
);

export default function AgentOSOverview() {
    const [data, setData] = useState(null);
    useEffect(() => { apiFetch("/overview").then((r) => (r.ok ? r.json() : null)).then(setData); }, []);

    return (
        <AgentOSLayout title="Mission Control" subtitle="Secure operational visibility for Afrinexa's agent registry, tasks, approvals, memory, usage and audit trail.">
            {!data ? <Spinner /> : (
                <div className="space-y-8" data-testid="agent-os-overview">
                    <div className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 xl:grid-cols-4">
                        <Stat icon={Bot} label="Registered agents" value={data.agents.total} testid="overview-agents" />
                        <Stat icon={Activity} label="Total tasks" value={data.tasks.total} testid="overview-tasks" />
                        <Stat icon={Clock3} label="Pending approvals" value={data.approvals.pending} testid="overview-approvals" />
                        <Stat icon={ShieldCheck} label="Tool calls today" value={data.usage_today.tool_calls} testid="overview-tools" />
                    </div>

                    <section className="border border-champagne/40 bg-champagne/10 p-6" data-testid="provider-status">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8a7320]">Provider status</p>
                        <p className="mt-3 font-serif text-2xl font-medium">No external AI provider is connected.</p>
                        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink/60">The operational demo uses the internal deterministic summarizer only. Cloud, self-hosted and local provider slots are registered but disabled until credentials and policies are configured.</p>
                    </section>

                    <div className="grid gap-8 xl:grid-cols-2">
                        <section className="border border-ink/10 bg-ivory p-6">
                            <div className="flex items-center justify-between"><h2 className="font-serif text-2xl font-medium">Recent tasks</h2><a href="/admin/agent-os/tasks" data-testid="overview-tasks-link" className="text-xs font-semibold uppercase tracking-[0.16em] text-forest">View all</a></div>
                            <div className="mt-5">
                                {data.recent_tasks.map((task) => (
                                    <div key={task.task_id} data-testid={`overview-task-${task.task_id}`} className="grid grid-cols-[1fr_auto] gap-4 border-t border-ink/10 py-4">
                                        <div className="min-w-0"><p className="truncate font-medium">{task.title}</p><p className="mt-1 text-xs text-ink/45">{task.agent_id} · {formatDateTime(task.created_at)}</p></div>
                                        <Pill value={task.status} />
                                    </div>
                                ))}
                                {data.recent_tasks.length === 0 && <p className="border-t border-ink/10 py-8 text-sm text-ink/50">No tasks yet.</p>}
                            </div>
                        </section>
                        <section className="border border-ink/10 bg-ivory p-6">
                            <div className="flex items-center justify-between"><h2 className="font-serif text-2xl font-medium">Pending approvals</h2><a href="/admin/agent-os/approvals" data-testid="overview-approvals-link" className="text-xs font-semibold uppercase tracking-[0.16em] text-forest">Review</a></div>
                            <div className="mt-5">
                                {data.pending_approvals.map((approval) => (
                                    <div key={approval.approval_id} data-testid={`overview-approval-${approval.approval_id}`} className="grid grid-cols-[1fr_auto] gap-4 border-t border-ink/10 py-4">
                                        <div className="min-w-0"><p className="truncate font-medium">{approval.action}</p><p className="mt-1 text-xs text-ink/45">{approval.agent_id} · {approval.tool_id || "no tool"}</p></div>
                                        <Pill value={approval.risk_level} risk />
                                    </div>
                                ))}
                                {data.pending_approvals.length === 0 && <p className="border-t border-ink/10 py-8 text-sm text-ink/50">No approvals waiting.</p>}
                            </div>
                        </section>
                    </div>
                </div>
            )}
        </AgentOSLayout>
    );
}
